import React, { useState, useEffect, useRef } from 'react';
import { tokens as defaultTokens } from '../tokens';
import MetaLabel from './MetaLabel';

export default function BreathingModal({ tokens = defaultTokens, onClose }) {
  const [phase, setPhase] = useState('in');
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [mounted, setMounted] = useState(false);

  // Trigger initial animation after mount
  const mountRef = useRef(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  useEffect(() => {
    const elapsed = 60 - secondsLeft;
    const cyclePos = elapsed % 16;
    const newPhase =
      cyclePos < 4  ? 'in' :
      cyclePos < 8  ? 'hold' :
      cyclePos < 12 ? 'out' : 'rest';
    setPhase(newPhase);
  }, [secondsLeft]);

  const phaseLabel = {
    in:   'Breathe in',
    hold: 'Hold',
    out:  'Breathe out',
    rest: 'Rest',
  }[phase];

  // Start small, expand on mount for "breathe in", then follow phase
  const orbScale = !mounted ? 0.55 : (phase === 'in' || phase === 'hold' ? 1.15 : 0.55);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: tokens.ground + 'f2',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      display: 'flex', flexDirection: 'column',
      height: '100dvh',
      overflow: 'hidden',
      animation: `gl-fade 300ms ${tokens.ease}`,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 20px 0',
        flexShrink: 0,
      }}>
        <MetaLabel tokens={tokens}>Breathing &middot; 60s</MetaLabel>
        <button className="gl-btn-bare" onClick={onClose} aria-label="Close" style={{
          width: 32, height: 32,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${tokens.edge}`, borderRadius: '50%',
          color: tokens.textMid,
          fontFamily: tokens.fontBody, fontSize: 16, lineHeight: 1,
        }}>&times;</button>
      </div>

      {/* Center section — phase label above orb */}
      <div style={{
        flex: 1, minHeight: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '0 20px',
      }}>
        {/* Phase label + countdown — above orb */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            fontFamily: tokens.fontDisplay, fontStyle: 'italic',
            fontSize: 32, color: tokens.textHi, letterSpacing: tokens.displayTracking,
          }}>{phaseLabel}</div>
          <div style={{
            fontFamily: tokens.fontBody, fontSize: 13,
            color: tokens.textLow, letterSpacing: '0.08em',
            marginTop: 10,
          }}>
            {secondsLeft > 0 ? `${secondsLeft}s left` : 'Done. Nice.'}
          </div>
        </div>

        {/* Orb — centered */}
        <div style={{ position: 'relative', width: 200, height: 200, flexShrink: 0 }}>
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            border: `1px solid ${tokens.accent}44`,
          }} />
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            width: 140, height: 140,
            transform: `translate(-50%, -50%) scale(${orbScale})`,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${tokens.accent}66 0%, ${tokens.accent}22 50%, transparent 70%)`,
            boxShadow: `0 0 60px ${tokens.accent}44`,
            transition: `transform 4s ${tokens.ease}`,
          }} />
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            width: 7, height: 7, borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            background: tokens.accent,
            boxShadow: `0 0 16px ${tokens.accent}`,
          }} />
        </div>

        {/* Instruction — below orb */}
        <p style={{
          fontFamily: tokens.fontDisplay, fontStyle: 'italic',
          fontSize: 15, color: tokens.textMid, lineHeight: 1.5,
          margin: 0, marginTop: 36, textAlign: 'center',
        }}>
          Four in. Four hold. Four out. Four rest.
          <br />
          Repeat until your shoulders drop.
        </p>
      </div>

      {/* Footer — close */}
      <div style={{ padding: '12px 24px 28px', textAlign: 'center', flexShrink: 0 }}>
        <button className="gl-btn-bare" onClick={onClose} style={{
          fontFamily: tokens.fontBody, fontSize: 13,
          color: tokens.textMid, letterSpacing: '0.08em',
          padding: '8px 18px',
          border: `1px solid ${tokens.edge}`,
        }}>Close</button>
      </div>
    </div>
  );
}
